// This file contains the notification maps moved from the cart project
// to break circular dependencies between cart and common-next

import { Domains, getLocale, Locale } from '@libs/core';

// Define the error keys as enums
export enum CartValidationErrorKey {
    PLACEHOLDER = 'placeholder.todo',
    UNEXPECTED_ERROR_BANNER = 'cart.checkout.unexpectederrorbanner',
    MIXED_CART_BANNER = 'cart.samedaydelivery.mixedcartbanner',
    UPDATE_FAILURE_BANNER = 'cart.checkout.updatefailurebanner',
    OUT_OF_STOCK = 'cart.checkout.outofstock',
    INSUFFICIENT_STOCK = 'cart.checkout.insufficientstock',
    PICKUP_STORE_CLOSED = 'cartcheckout.bopisdisabled',
}

export enum CheckoutValidationErrorKey {
    ADD_CARD_FAILURE_BANNER = 'checkout.addcardfailurebanner',
    PAYMENT_FAILURE = 'checkout.ordersubmit.paymentauthfailure',
    MARKET_SHIPPING_ADDRESS_ERROR = 'checkout.marketshippingaddresserror',
    PAYMENT_SERVICE_ERROR = 'checkout.paymentserviceerror',
    SDD_WEIGHT_LIMIT = 'sdd-weight-limit',
}

// Define the notification types
export type NotificationId =
    | 'quantity-adjusted'
    | 'autoship-subscription'
    | 'mixed-cold-item'
    | 'multiple-pharmacy-items'
    | 'multi-store-bopis'
    | CartValidationErrorKey
    | CheckoutValidationErrorKey;

export type Notification = {
    id: NotificationId;
    variant: 'info' | 'warning' | 'success' | 'error';
    header: string;
    text: string;
    list?: string[];
    closable?: boolean;
};

// Utility functions to create notification objects
export const createGenericErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.PLACEHOLDER,
    variant: 'error',
    header: 'Something went wrong',
    text: 'Please refresh the page and try again',
});

export const createUnexpectedErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.UNEXPECTED_ERROR_BANNER,
    variant: 'error',
    header: 'An unexpected error occurred.',
    text: 'Please try again later.',
});

export const createMixedCartBannerErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.MIXED_CART_BANNER,
    variant: 'error',
    header: 'Delivery error',
    text: 'Please update all items to same-day delivery or remove items from cart to proceed to checkout',
});

export const createPickupStoreErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.PICKUP_STORE_CLOSED,
    variant: 'error',
    header: 'Pickup store temporarily closed,',
    text: 'for one or more items in your cart. Please update selected store',
});

export const createPaymentUnavailableErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.PAYMENT_FAILURE,
    variant: 'error',
    header: 'Payment unavailable',
    text: 'One or more payment methods are not available at this time. Please select a different method to continue',
});

export const createMarketShippingAddressErrorNotification =
    (): Notification => {
        const domainMapping = {
            [Locale['en-US']]: {
                countryName: 'US',
                targetDomain: Domains['en-CA'].replace('https://www.', ''),
            },
            [Locale['en-CA']]: {
                countryName: 'Canada',
                targetDomain: Domains['en-US'].replace('https://www.', ''),
            },
        };

        const { targetDomain, countryName } = domainMapping[getLocale()];

        return {
            id: CheckoutValidationErrorKey.MARKET_SHIPPING_ADDRESS_ERROR,
            variant: 'error',
            header: 'Invalid address',
            text: `Please navigate to ${targetDomain} or update your shipping address to a ${countryName} address`,
        };
    };

// Define the notification maps using the utility functions
export const cartValidationNotificationMap: Partial<
    Record<CartValidationErrorKey, Notification>
> = {
    [CartValidationErrorKey.PLACEHOLDER]: createGenericErrorNotification(),
    [CartValidationErrorKey.UNEXPECTED_ERROR_BANNER]: {
        ...createGenericErrorNotification(),
        id: CartValidationErrorKey.UNEXPECTED_ERROR_BANNER,
    },
    [CartValidationErrorKey.UPDATE_FAILURE_BANNER]: {
        ...createGenericErrorNotification(),
        id: CartValidationErrorKey.UPDATE_FAILURE_BANNER,
    },
    [CartValidationErrorKey.MIXED_CART_BANNER]:
        createMixedCartBannerErrorNotification(),
    [CartValidationErrorKey.PICKUP_STORE_CLOSED]:
        createPickupStoreErrorNotification(),
};

export const checkoutValidationNotificationMap: Partial<
    Record<CheckoutValidationErrorKey | CartValidationErrorKey, Notification>
> = {
    [CheckoutValidationErrorKey.ADD_CARD_FAILURE_BANNER]: {
        ...createUnexpectedErrorNotification(),
        id: CheckoutValidationErrorKey.ADD_CARD_FAILURE_BANNER,
    },
    [CheckoutValidationErrorKey.PAYMENT_FAILURE]:
        createPaymentUnavailableErrorNotification(),
    [CheckoutValidationErrorKey.MARKET_SHIPPING_ADDRESS_ERROR]:
        createMarketShippingAddressErrorNotification(),
    [CheckoutValidationErrorKey.PAYMENT_SERVICE_ERROR]:
        createPaymentUnavailableErrorNotification(),
};

// Combined map for convenience
export const validationNotificationMap = {
    ...cartValidationNotificationMap,
    ...checkoutValidationNotificationMap,
};

// Array of all validation error keys
export const CART_VALIDATION_ERROR_KEYS: Array<
    CartValidationErrorKey | CheckoutValidationErrorKey
> = [
    ...Object.values(CartValidationErrorKey),
    ...Object.values(CheckoutValidationErrorKey),
];
